'use client';

import { Suspense, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { supabase } from '@/supabase/client';
import { getSiteUrl } from '@/lib/auth/getSiteUrl';
import { validatePublicDisplayName } from '@/lib/displayName';

export const dynamic = 'force-dynamic';

function normalizeReferralCode(ref: string | null | undefined): string | null {
  if (!ref) return null;
  const normalized = ref.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (normalized.length < 6 || normalized.length > 16) return null;
  return normalized;
}

function SignupInner() {
  const router = useRouter();
  const params = useSearchParams();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    
    const displayNameResult = validatePublicDisplayName(displayName);
    if (!displayNameResult.ok) {
      setMessage(displayNameResult.error);
      setLoading(false);
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Validate password strength
    if (password.length < 6) {
      setMessage('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }
    
    try {
      // Use getSiteUrl() to ensure correct origin (production or localhost)
      const siteUrl = getSiteUrl();
      const redirectTo = `${siteUrl}/portal/onboarding`;

      const normalizedReferralCode = normalizeReferralCode(params?.get('ref'));

      const signUpMetadata: Record<string, string> = {
        email,
        full_name: displayNameResult.value,
      };
      if (normalizedReferralCode) {
        signUpMetadata.referral_code = normalizedReferralCode;
      }

      console.log('[Signup] signUp metadata:', signUpMetadata);

      console.log('📧 Signup: Requesting confirmation email:', {
        email,
        redirectTo,
        siteUrl,
      });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectTo,
          data: signUpMetadata,
        },
      });

      if (error) {
        console.error('❌ Signup email delivery failed:', {
          error: error.message,
          code: error.status,
          email,
          redirectTo,
          fullError: error,
        });
        setMessage(error.message);
      } else {
        console.log('✅ Signup response:', {
          email,
          redirectTo,
          hasSession: !!data.session,
        });

        // If session is created immediately (email confirmation disabled), redirect to onboarding
        if (data.session) {
          console.log('✅ Session created immediately, redirecting to onboarding');
          setMessage('Account created with 6 months of Plus! Redirecting...');
          // Small delay to show message, then redirect
          setTimeout(() => {
            router.replace('/portal/onboarding');
          }, 1000);
        } else {
          // Email confirmation required
          console.log('✅ Signup confirmation email sent successfully:', {
            email,
            redirectTo,
          });
          setMessage('Check your email to confirm your account. Your 6 months of Plus are added automatically.');
        }
      }
    } catch (err) {
      console.error('❌ Signup exception:', {
        error: err instanceof Error ? err.message : 'Unknown error',
        email,
        fullError: err,
      });
      setMessage('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main style={{ padding: 16, maxWidth: 420, margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 12px' }}>Sign up</h1>
      <section
        aria-label="Founding member offer"
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 10,
          border: '1px solid #7dd3fc',
          background: '#f0f9ff',
          color: '#0c4a6e',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 4 }}>
          Get 6 months of Plus free
        </strong>
        <span style={{ fontSize: 13, lineHeight: 1.45 }}>
          Create your account by December 31, 2026. No card, no code, and no automatic renewal.
        </span>
      </section>
      
      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 8 }}>
        <label htmlFor="public-display-name" style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
          Name shown on Off Axis Deals
        </label>
        <input
          id="public-display-name"
          type="text"
          value={displayName}
          required
          minLength={2}
          maxLength={80}
          autoComplete="name"
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your name, business, or brand"
          aria-describedby="public-display-name-help"
          style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px' }}
        />
        <p id="public-display-name-help" style={{ fontSize: 12, color: '#6b7280', margin: '0 0 4px' }}>
          Buyers and sellers may see this name. Use a personal, business, or brand name—not your email address.
        </p>

        <label style={{ fontSize: 13, color: '#374151' }}>Email</label>
        <input
          type="email"
          value={email}
          required
          onChange={(e) => setEmail(e.target.value)}
          style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px' }}
        />
        
        <label style={{ fontSize: 13, color: '#374151' }}>Password</label>
        <input
          type="password"
          value={password}
          required
          onChange={(e) => setPassword(e.target.value)}
          style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px' }}
        />
        
        <label style={{ fontSize: 13, color: '#374151' }}>Confirm Password</label>
        <input
          type="password"
          value={confirmPassword}
          required
          onChange={(e) => setConfirmPassword(e.target.value)}
          style={{ height: 40, border: '1px solid #cbd5e1', borderRadius: 8, padding: '0 10px' }}
        />
        
        <button
          disabled={loading}
          style={{ height: 40, borderRadius: 8, border: '1px solid #0ea5e9', background: '#0ea5e9', color: '#fff' }}
        >
          {loading ? 'Creating account…' : 'Create account — 6 months Plus free'}
        </button>
        
        {message && (
          <p style={{ 
            fontSize: 13, 
            color: message.includes('error') || message.includes('Error') ? '#dc2626' : '#374151' 
          }}>
            {message}
          </p>
        )}
        
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8, textAlign: 'center' }}>
          Already have an account? <a href="/login" style={{ color: '#0ea5e9' }}>Sign in</a>
        </p>
      </form>
    </main>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<main style={{ padding: 16 }}>Loading…</main>}>
      <SignupInner />
    </Suspense>
  );
}
